import { ChangedBadge } from "../common/changed-badge";
import { PersonRole } from "../common/person-role";
import { ACCOMMODATION_SEARCH_MAX, HELPDESK_OPEN_STATUSES, ROOMING_PEOPLE_SORT_KEYS, ROOMING_PERSON_STATES, formatAccreditationNo } from "@seagames/contracts";
import { type ColumnDef, DataTable, Icons, useColumnVisibility, ListHeaderButton, ListToolbar, PrimaryCell, Select, type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { contingentPeopleOptions, contingentsOptions, downloadRoomingCsv } from "../../lib/api/accommodation";
import { helpdeskTicketsOptions } from "../../lib/api/helpdesk";
import { type RoomingContingent, type RoomingOrgRef, type RoomingPerson, type RoomingPersonState, stayOf } from "../../lib/accommodation/types";
import { useDefaultGamesId } from "../../lib/competition/games";
import { errorMessage } from "../../lib/api/messages";
import type { SearchNavigate } from "../../lib/list-page/use-list-page";
import { nocModuleOpenFor } from "../../lib/noc-stages/access";
import { groupGrants } from "../../lib/travel/access";
import { toast } from "../../lib/ui/toast";
import { Bar, SexLetter, StayRange } from "../accommodation/parts";
import { PersonStateChip, PersonStateDot, useRoomingGuide } from "../accommodation/status";
import { EmptyCard } from "../common/empty-card";
import { StatusGuide } from "../common/status-guide";
import { BedIcon, DownloadIcon, NewTicketModal, TicketModal, TicketRow, useTicketGuideGroup } from "../helpdesk";
import { reportableRoomsOptions } from "./api";
import { lenient, useViewSearch } from "./use-view-search";

/**
 * A NOC's Accommodation page (her `#/nrm`, PLAN §4 "NOC rooming"), mounted
 * by S4's `/accommodation` index for an ORGANIZATION session — no props.
 *
 * Read-only (PLAN A13): the strip, its own rooming list (search on Enter,
 * state chips, sort, CSV), "Report a room problem" (rooms its
 * own people stay in) and its room problems. A contingent holding more than
 * one organisation gets a picker.
 *
 * Privacy (M10 §0b, PLAN §0b): every read is the API's `cardInScope` over
 * the NOC's OWN people — the list never names a roommate from another
 * contingent, a building's other guests or its occupancy. Nothing here adds
 * any of that back; the room column is the person's own room number.
 *
 * URL (`nocAccommodationSearchShape`, spread by S4 into the index route):
 *   org  the organisation, when the login reads several
 *   state, search, order, page, page_size  the rooming list (`order` =
 *   the API's `sort`: name | team | room; absent = team, hers)
 */

export const nocAccommodationSearchShape = {
	games_id: lenient(z.string().uuid()),
	org: lenient(z.string().uuid()),
	state: lenient(z.enum(ROOMING_PERSON_STATES)),
	search: lenient(z.string().trim().min(1).max(ACCOMMODATION_SEARCH_MAX)),
	order: lenient(z.enum(ROOMING_PEOPLE_SORT_KEYS)),
	page: lenient(z.coerce.number().int().min(1)),
	page_size: lenient(z.coerce.number().int().min(1).max(100)),
};

/** Her default: 50 a page. */
const PAGE_SIZE_DEFAULT = 50;
const PAGE_SIZES = [25, 50, 100] as const;

export function NocAccommodation() {
	const { t } = useTranslation("helpdesk");
	const { session } = useRouteContext({ from: "/_app" });
	const { search, navigate } = useViewSearch(nocAccommodationSearchShape);
	const gamesId = useDefaultGamesId(search.games_id);
	const contingents = useQuery({ ...contingentsOptions(gamesId ?? ""), enabled: !!gamesId });
	const rows = contingents.data?.items ?? [];
	const row = rows.find((r) => r.responsible_organization.responsible_organization_id === search.org) ?? rows[0];

	if (!gamesId || contingents.isPending)
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[20rem] rounded-[0.625rem]" />
			</div>
		);
	if (contingents.isError)
		return (
			<EmptyCard
				tone="bad"
				title={t("noc.loadFailed")}
				actions={
					<button type="button" className="btn btn-sm btn-line" onClick={() => void contingents.refetch()}>
						{t("common.retry")}
					</button>
				}
			/>
		);
	if (!row)
		return (
			<EmptyCard icon={<BedIcon />} title={t("noc.noPeopleTitle")}>
				{t("noc.noPeopleDescription")}
			</EmptyCard>
		);

	// M7c: room problems are helpdesk tickets — hidden (and never fetched) until the NOC's stage opens the helpdesk.
	const canReport = groupGrants(session, "helpdesk.report") && nocModuleOpenFor(session, "helpdesk");
	return (
		<NocRooming
			key={row.responsible_organization.responsible_organization_id}
			gamesId={gamesId}
			orgs={rows.map((r) => r.responsible_organization)}
			row={row}
			canReport={canReport}
			defaultContact={session.user.name}
			search={search}
			navigate={navigate}
		/>
	);
}

type Search = z.infer<z.ZodObject<typeof nocAccommodationSearchShape>>;

function NocRooming({
	gamesId,
	orgs,
	row,
	canReport,
	defaultContact,
	search,
	navigate,
}: {
	gamesId: string;
	orgs: RoomingOrgRef[];
	row: RoomingContingent;
	canReport: boolean;
	defaultContact: string;
	search: Search;
	navigate: SearchNavigate<Search>;
}) {
	const { t } = useTranslation("helpdesk");
	const { t: ta } = useTranslation("accommodation");
	const { formatNumber } = useUiStrings();
	const orgId = row.responsible_organization.responsible_organization_id;
	const [reporting, setReporting] = useState(false);
	const [opened, setOpened] = useState<string | null>(null);
	const [downloading, setDownloading] = useState(false);
	const guide = useRoomingGuide({ rooms: false });
	const ticketGroup = useTicketGuideGroup(t("guide.roomProblems"));

	const sort = search.order ?? "team";
	const pageNo = search.page ?? 1;
	const pageSize = search.page_size ?? PAGE_SIZE_DEFAULT;
	const list = useQuery(contingentPeopleOptions(orgId, { games_id: gamesId, state: search.state, q: search.search, sort, page: pageNo, page_size: pageSize }));
	const columnVisibility = useColumnVisibility("accommodation-noc-rooming");
	/** Write URL params; back to page 1 (filters, sort, page size). */
	const patch = (next: Partial<Search>) => void navigate({ search: (p) => ({ ...p, page: undefined, ...next }), replace: true });

	// Room problems: its own organisation's tickets (the API scopes a NOC to them anyway).
	const openTickets = useQuery({
		...helpdeskTicketsOptions({ games_id: gamesId, responsible_organization_id: orgId, status: HELPDESK_OPEN_STATUSES.join(","), limit: 1 }),
		enabled: canReport,
	});
	const tickets = useQuery({ ...helpdeskTicketsOptions({ games_id: gamesId, responsible_organization_id: orgId, limit: 10 }), enabled: canReport });
	const rooms = useQuery(reportableRoomsOptions(orgId, gamesId, canReport && reporting));

	const people = row.approved;
	const cells: StatsCell[] = [
		{ key: "people", label: t("noc.strip.people"), value: people },
		{
			key: "bed",
			label: t("noc.strip.haveBed"),
			value: row.with_bed,
			note: <Bar value={row.with_bed} max={people} tone="ok" className="mt-[0.25rem] w-full max-w-[9rem]" />,
		},
		{ key: "in", label: t("noc.strip.checkedIn"), value: row.checked_in },
		{ key: "none", label: t("noc.strip.withoutBed"), value: row.without_bed, note: row.without_bed > 0 ? t("noc.strip.hostAssigning") : t("noc.strip.none") },
		...(canReport
			? [{ key: "tickets", label: t("noc.strip.roomProblems"), value: openTickets.data?.total ?? "—", note: t("noc.strip.open") } satisfies StatsCell]
			: []),
	];

	const download = async () => {
		setDownloading(true);
		try {
			await downloadRoomingCsv(orgId, { games_id: gamesId }, t("noc.csvFilename", { org: row.responsible_organization.code ?? "contingent" }));
		} catch (err) {
			toast.error(err, t("noc.downloadFailed"));
		} finally {
			setDownloading(false);
		}
	};

	const columns: ColumnDef<RoomingPerson>[] = [
		{
			key: "name",
			header: t("noc.list.name"),
			lockVisible: true,
			minWidth: "14rem",
			cell: (p) => (
				<PrimaryCell
					sub={
						<span className="inline-flex items-center gap-[0.375rem]">
							<span className="tabular-nums">{p.accreditation_no !== null ? formatAccreditationNo(p.organization_code, p.accreditation_no) : "—"}</span>
							{p.gender && (
								<>
									<span aria-hidden>·</span>
									<SexLetter sex={p.gender} />
								</>
							)}
							<PersonRole category={p.main_function} fn={p.sub_function} />
							{p.changed && <ChangedBadge />}
						</span>
					}
				>
					{p.name}
				</PrimaryCell>
			),
		},
		{ key: "team", header: t("noc.list.team"), minWidth: "8rem", cell: (p) => <span className="text-gms-ink-2">{p.team ?? t("noc.headquarters")}</span> },
		{
			key: "room",
			header: t("noc.list.room"),
			minWidth: "10rem",
			cell: (p) =>
				p.assignment ? (
					<span className="grid leading-tight">
						<b className="font-semibold text-gms-navy-ink tabular-nums">{p.assignment.room_label}</b>
						<span className="text-[0.75rem] text-gms-ink-3">{p.assignment.building.name}</span>
					</span>
				) : (
					<span className="text-gms-ink-3">{t("noc.list.notAssigned")}</span>
				),
		},
		{ key: "stay", header: t("noc.list.stay"), minWidth: "11rem", cell: (p) => <StayRange stay={p.assignment ? stayOf(p.assignment) : null} /> },
		{ key: "status", header: t("noc.list.status"), minWidth: "7rem", cell: (p) => <PersonStateChip state={p.state} /> },
	];

	const filtered = !!search.state || !!search.search;
	const clear = () => patch({ state: undefined, search: undefined });

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{orgs.length > 1 && (
				<div className="flex flex-wrap items-center gap-[0.75rem]">
					<label htmlFor="noc-org" className="text-[0.8125rem] font-medium text-gms-ink-2">
						{t("noc.org")}
					</label>
					<Select
						id="noc-org"
						size="sm"
						className="min-w-[16rem]"
						options={orgs.map((o) => ({ value: o.responsible_organization_id, label: o.name }))}
						value={orgId}
						onChange={(v) => v && void navigate({ search: (p) => ({ ...p, org: v, page: undefined, state: undefined, search: undefined }), replace: true })}
					/>
				</div>
			)}

			<StatsStrip label={t("noc.strip.label")} cells={cells} />

			<header className="flex flex-wrap items-center justify-between gap-x-[1rem] gap-y-[0.625rem]">
				<div className="min-w-0 flex-[1_1_16rem]">
					<h2 className="flex flex-wrap items-baseline gap-x-[0.5rem] text-[0.96875rem] leading-tight font-semibold text-gms-navy-ink">
						{t("noc.title")}
						{list.data && (
							<span className="text-[0.78125rem] font-normal text-gms-ink-3 tabular-nums">
								{t("noc.count", { count: list.data.total, n: formatNumber(list.data.total) })}
							</span>
						)}
					</h2>
					<p className="mt-[0.25rem] text-[0.8125rem] leading-snug text-gms-ink-3">{t("noc.intro")}</p>
				</div>
				<div className="flex flex-wrap gap-[0.5rem] max-[44.999rem]:w-full">
					<ListHeaderButton tone="line" icon={downloading ? <span className="loading loading-spinner loading-xs" /> : <DownloadIcon />} onClick={() => void download()} className="max-[44.999rem]:flex-1">
						{t("noc.download")}
					</ListHeaderButton>
					{canReport && (
						<ListHeaderButton tone="primary" icon={<Icons.Alert />} onClick={() => setReporting(true)} className="max-[44.999rem]:flex-1">
							{t("report.open")}
						</ListHeaderButton>
					)}
				</div>
			</header>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => patch({ search: v || undefined })}
				searchPlaceholder={t("noc.list.search")}
				sort={{
					value: sort,
					options: ROOMING_PEOPLE_SORT_KEYS.map((k) => ({ value: k, label: t(`noc.list.sort.${k}`) })),
					onChange: (v) => patch({ order: v === "team" ? undefined : (v as Search["order"]) }),
				}}
				end={<StatusGuide title={guide.title} groups={canReport ? [...guide.groups, ticketGroup] : guide.groups} />}
				chips={[
					{
						kind: "options",
						key: "state",
						label: t("noc.list.stateChips"),
						options: ROOMING_PERSON_STATES.map((s) => ({ value: s, label: ta(`person.state.${s}`), icon: <PersonStateDot state={s} /> })),
						value: search.state ? [search.state] : [],
						onChange: (v) => patch({ state: (v[0] as RoomingPersonState | undefined) ?? undefined }),
					},
				]}
				active={filtered}
				onClearAll={clear}
			/>

			<DataTable<RoomingPerson>
				rows={list.data?.items ?? []}
				loading={list.isFetching}
				error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
				columnVisibility={columnVisibility}
				pagination={{
					page: pageNo,
					pageSize,
					total: list.data?.total ?? 0,
					pageSizeOptions: PAGE_SIZES,
					labels: {
						summary: ({ from, to, total }) => t("noc.list.range", { count: total, from: formatNumber(from), to: formatNumber(to), total: formatNumber(total) }),
						empty: t("noc.list.rangeEmpty"),
					},
					onPageChange: (n) => void navigate({ search: (p) => ({ ...p, page: n === 1 ? undefined : n }) }),
					onPageSizeChange: (n) => patch({ page_size: n === PAGE_SIZE_DEFAULT ? undefined : n }),
				}}
				variant="list"
				columns={columns}
				rowKey={(p) => p.ac_card_id}
				empty={
					filtered
						? {
								title: t("noc.list.emptyFilteredTitle"),
								description: t("noc.list.emptyFilteredDescription"),
								action: (
									<button type="button" className="btn btn-sm btn-outline" onClick={clear}>
										{t("common.clearFilters")}
									</button>
								),
							}
						: { title: t("noc.list.emptyTitle"), description: t("noc.list.emptyDescription") }
				}
			/>

			{canReport && (
				<section aria-labelledby="noc-room-problems" className="grid gap-[0.625rem]">
					<div className="flex flex-wrap items-center justify-between gap-[0.5rem]">
						<h2 id="noc-room-problems" className="text-[0.96875rem] font-semibold text-gms-navy-ink">
							{t("noc.problems.title")}
						</h2>
						{tickets.data?.next_cursor && (
							<Link to="/accommodation/helpdesk" search={{ responsible_organization_id: orgId, status: "any" }} className="text-[0.8125rem] font-semibold text-gms-navy hover:underline">
								{t("noc.problems.all")}
							</Link>
						)}
					</div>
					{tickets.isPending ? (
						<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
					) : (tickets.data?.items.length ?? 0) === 0 ? (
						<EmptyCard icon={<Icons.Check />} title={t("noc.problems.emptyTitle")}>
							{t("noc.problems.emptyDescription")}
						</EmptyCard>
					) : (
						<ul className="grid gap-[0.5rem]">
							{tickets.data?.items.map((tk) => (
								<li key={tk.helpdesk_ticket_id}>
									<TicketRow
										row={tk}
										showOrg={false}
										icon={<BedIcon />}
										onOpen={() => setOpened(tk.helpdesk_ticket_id)}
										note={
											tk.last_note ? (
												<span className="text-[0.78125rem] text-gms-ink-2">
													{t("noc.problems.hostNote", {
														note: tk.last_note.body ?? (tk.last_note.status_to ? t("ticket.statusNote", { status: t(`status.label.${tk.last_note.status_to}`) }) : ""),
													})}
												</span>
											) : undefined
										}
									/>
								</li>
							))}
						</ul>
					)}
				</section>
			)}

			{reporting && (
				<NewTicketModal
					mode="noc"
					gamesId={gamesId}
					organizationId={orgId}
					rooms={rooms.data?.items ?? []}
					defaultContact={defaultContact}
					onClose={() => setReporting(false)}
				/>
			)}
			{opened && <TicketModal ticketId={opened} onClose={() => setOpened(null)} />}
		</div>
	);
}
